<script>
  import { API_BASE_URL, EMAIL_CONFIG, isSuperAdmin as checkSuperAdmin, isAdminUser } from './config.js';
  import { onMount, onDestroy } from 'svelte';
  import Navigation from './components/layout/Navigation.svelte';
  import LoadingSpinner from './components/common/LoadingSpinner.svelte';
  import ErrorMessage from './components/common/ErrorMessage.svelte';
  import EmptyState from './components/common/EmptyState.svelte';
  import BaseModal from './components/modals/BaseModal.svelte';
  import FormModal from './components/modals/FormModal.svelte';
  import Button from './components/common/Button.svelte';

  export let setPage;
  export let user;

  let isLoading = false;
  let loadError = null;
  let applicants = [];
  let isAdmin = false;
  let isSuperAdmin = false;
  let isCompany = false;
  let isMobile = false;
  let applicantSearchTerm = "";
  let filteredAllApplicants = [];
  let allRawApplicants = [];
  let selectedApplicantForList = null;
  let applicantProjects = [];
  let isDetailModalOpen = false;
  let selectedApplicant = null;
  let isEmailModalOpen = false;
  let emailSubject = '';
  let emailContent = '';
  let isSendingEmail = false;
  let emailSendError = null;
  let emailSendSuccess = false;
  let isProfileFilesModalOpen = false;
  let selectedApplicantFiles = [];
  let isLoadingFiles = false;
  let fileLoadError = null;
  let showExcludedUsers = false;
  let previousApplicationStatus = {};
  let isProjectDetailModalOpen = false;
  let selectedProjectForDetail = null;

  const emailConfig = EMAIL_CONFIG;
  const statusOptions = [
    { id: 0, label: '신청됨' }, { id: 1, label: '검토중' }, { id: 9, label: '본인확인중' },
    { id: 2, label: '진행중' }, { id: 3, label: '합격' }, { id: 4, label: '불합격' },
    { id: 5, label: '프로젝트 취소' }, { id: 6, label: '프로젝트 연기' }, { id: 7, label: '마감' },
    { id: 8, label: '지원 취소' }, { id: 10, label: '진행 어려움(스킬)' }, { id: 11, label: '진행 어려움(경험)' }
  ];
  const statusMapping = {
    'applied': 0, 'reviewing': 1, 'proceeding': 2, 'accepted': 3, 'rejected': 4,
    'project_canceled': 5, 'project_delayed': 6, 'closed': 7, 'canceled': 8, 'verifying': 9,
    'price_difficulty': 10, 'grade_difficulty': 11
  };

  function checkMobileDevice() {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }

  function handleResize() {
    isMobile = checkMobileDevice();
  }




  function formatPrice(price) {
    if (!price) return '-';
    const numericPrice = String(price).replace(/[^\d]/g, '');
    return numericPrice ? Number(numericPrice).toLocaleString() : '-';
  }

  function isUserExcluded(user) {
    if (!user) return false;
    const val = user.is_excluded ?? user.isExcluded;
    if (typeof val === 'string') return ['Y', 'TRUE', '1'].includes(val.toUpperCase());
    return val === true || val === 1;
  }

  onMount(async () => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          user = JSON.parse(storedUser);
          isAdmin = isAdminUser(user);
          isSuperAdmin = checkSuperAdmin(user.email);
          isCompany = user.role === 'company';
        } catch (e) { localStorage.removeItem('user'); }
      }
      window.addEventListener('resize', handleResize);
      handleResize();
    }
    await fetchAllApplicants();
  });

  onDestroy(() => {
    if (typeof window !== 'undefined') window.removeEventListener('resize', handleResize);
  });

  $: {
    if (allRawApplicants) {
      const baseData = showExcludedUsers ? allRawApplicants : allRawApplicants.filter(app => !isUserExcluded(app.userInfo) && !isUserExcluded(app.raw));
      const uniqueApplicantsMap = new Map();
      baseData.forEach(app => {
        const key = app.userId || app.email;
        if (!uniqueApplicantsMap.has(key)) {
          uniqueApplicantsMap.set(key, { ...app, projectCount: 0 });
        }
        uniqueApplicantsMap.get(key).projectCount++;
      });
      const uniqueApplicants = Array.from(uniqueApplicantsMap.values());

      if (applicantSearchTerm.trim() === "") {
        filteredAllApplicants = uniqueApplicants;
      } else {
        const searchLower = applicantSearchTerm.toLowerCase();
        filteredAllApplicants = uniqueApplicants.filter(a => a.name?.toLowerCase().includes(searchLower) || a.email?.toLowerCase().includes(searchLower));
      }
    }
  }

  function clearSearch() {
    applicantSearchTerm = "";
  }

  async function fetchAllApplicants() {
    isLoading = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/applications/all`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('전체 지원자 목록 로드 실패');
      const data = await response.json();
      const applications = data.applications || (Array.isArray(data) ? data : []);
      const users = await fetchUsersData();
      processApplicationsWithUsers(applications, users);
    } catch (e) {
      loadError = e.message;
    } finally {
      isLoading = false;
    }
  }

  async function fetchUsersData() {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) return [];
      const data = await response.json();
      return data.users || (Array.isArray(data) ? data : []);
    } catch (e) { return []; }
  }

  function processApplicationsWithUsers(applications, users) {
    const userMap = new Map(users.map(u => [String(u.id || u._id), u]));
    allRawApplicants = applications.map(app => {
      const userId = String(app.userId || app.user_id);
      const userInfo = userMap.get(userId);
      return {
        id: String(app.id || app._id),
        userId,
        name: userInfo?.name || app.name || '이름 없음',
        email: userInfo?.email || app.email || '-',
        isBlacklist: userInfo?.is_blacklist === 'Y' || app.is_blacklist === 'Y',
        userInfo,
        raw: app
      };
    });
  }

  async function fetchApplicantProjects(applicant) {
    if (!applicant || !applicant.userId) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${applicant.userId}/applications`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('지원 프로젝트 목록 로드 실패');
      const data = await response.json();
      const apps = data.applications || (Array.isArray(data) ? data : []);
      const projectIds = [...new Set(apps.map(a => a.projectId))].filter(Boolean);
      const projectsData = await fetchProjectsData(projectIds);
      const projectMap = new Map(projectsData.map(p => [String(p.id), p]));

      applicantProjects = apps.map(app => ({
        ...app,
        projectTitle: projectMap.get(String(app.projectId))?.title || '프로젝트명 없음'
      }));
    } catch (e) {
      loadError = e.message;
    }
  }

  async function fetchProjectsData(projectIds) {
    if (projectIds.length === 0) return [];
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) return [];
      const data = await response.json();
      const allProjects = data.projects || (Array.isArray(data) ? data : []);
      return allProjects.filter(p => projectIds.includes(String(p.id)));
    } catch (e) { return []; }
  }

  function viewApplicantDetail(applicant) {
    selectedApplicant = applicant;
    fetchApplicantProjects(applicant);
    isDetailModalOpen = true;
  }

  function updateApplicantStatus(applicant, newStatus) {
    // Status update logic would go here
    console.log('Status update:', applicant, newStatus);
  }

  function openProjectDetailModal(projectId) {
    // Project detail modal logic would go here
    console.log('Open project detail:', projectId);
  }



</script>

<svelte:head>
  <title>전체 지원자 관리 - WIDE-WORK</title>
</svelte:head>

<div class="admin-dashboard">
  <Navigation {setPage} {user} />

  <main class="main-content">
    <div class="container">
      <div class="page-header">
        <h1>전체 지원자 관리</h1>
        <div class="header-actions">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={showExcludedUsers}>
            제외된 회원 보기
          </label>
        </div>
      </div>

      {#if loadError}
        <ErrorMessage message={loadError} />
      {/if}

      <div class="content-wrapper">
        <div class="project-list-container">
          <div class="project-list-header">
            전체 지원자 ({filteredAllApplicants.length}명)
          </div>
          <div class="search-box">
            <div class="search-input-wrapper">
              <input type="text" placeholder="지원자 이름 또는 이메일 검색..." bind:value={applicantSearchTerm} />
              <button class="clear-search-btn" on:click={clearSearch}>×</button>
            </div>
          </div>
          <div class="project-list">
            {#if isLoading && filteredAllApplicants.length === 0}
              <LoadingSpinner />
            {:else if filteredAllApplicants.length > 0}
              <ul>
                {#each filteredAllApplicants as applicant, i (applicant.userId || i)}
                  <li>
                    <button on:click={() => { selectedApplicantForList = applicant; fetchApplicantProjects(applicant); }} class="project-item" class:selected={selectedApplicantForList && selectedApplicantForList.userId === applicant.userId}>
                      <div class="project-info">
                        <div class="project-main-info">
                          <span class="project-title">{applicant.name}</span>
                          <div class="company-row">
                            <span class="company-name">{applicant.email}</span>
                            <span class="applicant-count">지원 {applicant.projectCount}건</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                {/each}
              </ul>
            {:else}
              <EmptyState message="표시할 지원자가 없습니다." />
            {/if}
          </div>
        </div>

        <div class="applicant-list-container">
          {#if selectedApplicantForList}
            <div class="applicant-header">
              <h2>{selectedApplicantForList.name} - 지원 프로젝트 목록</h2>
            </div>
            <div class="projects-table-container">
              {#if isMobile}
                <div class="mobile-projects-cards">
                  {#each applicantProjects as project (project.id)}
                    <div class="mobile-project-card">
                      <div class="card-header">
                        <span class="project-title-text">{project.projectTitle || '프로젝트명 없음'}</span>
                      </div>
                      <div class="card-body">
                        <div class="card-row">
                          <strong>단가(만원):</strong> {formatPrice(project.price)}
                        </div>
                        <div class="card-row">
                          <strong>지원일자:</strong> {(project.applied_at || project.appliedAt || project.createdAt) ? new Date(project.applied_at || project.appliedAt || project.createdAt).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '') : '-'}
                        </div>
                        <div class="card-row status-row">
                          <strong>상태:</strong>
                          <select class="mobile-status-select" on:change={(e) => updateApplicantStatus(project, e.target.value)}>
                            {#each statusOptions as option}
                              <option value={option.id} selected={option.id === project.status}>{option.label}</option>
                            {/each}
                          </select>
                        </div>
                        <div class="card-row actions-row">
                          <button class="detail-btn" on:click={() => viewApplicantDetail(project)}>📋 상세</button>
                        </div>
                      </div>
                    </div>
                  {/each}
                </div>
              {:else}
                <table class="projects-table">
                  <thead>
                    <tr>
                      <th>공고명</th>
                      <th>단가(만원)</th>
                      <th>지원일자</th>
                      <th>상태</th>
                      <th>바로가기</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each applicantProjects as project (project.id)}
                      <tr>
                        <td>
                          <button class="project-title-link" on:click={() => openProjectDetailModal(project.projectId)}>
                            {project.projectTitle || '프로젝트명 없음'}
                          </button>
                        </td>
                        <td>{formatPrice(project.price)}</td>
                        <td>{(project.applied_at || project.appliedAt || project.createdAt) ? new Date(project.applied_at || project.appliedAt || project.createdAt).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '') : '-'}</td>
                        <td>
                          <select class="status-select" on:change={(e) => updateApplicantStatus(project, e.target.value)}>
                            {#each statusOptions as option}
                              <option value={option.id} selected={option.id === project.status}>{option.label}</option>
                            {/each}
                          </select>
                        </td>
                        <td>
                          <button class="detail-btn small" on:click={() => viewApplicantDetail(project)}>📋 상세</button>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              {/if}
            </div>
          {:else}
            <EmptyState title="지원자를 선택하여 지원한 프로젝트를 확인하세요." />
          {/if}
        </div>
      </div>
    </div>
  </main>
</div>

<!-- Modals -->
{#if isDetailModalOpen}
  <BaseModal on:close={() => isDetailModalOpen = false}>
    <div class="applicant-detail-modal">
      {#if selectedApplicant}
        <h2>{selectedApplicant.name}님 상세 정보</h2>
        <!-- ... 상세 정보 내용 ... -->
      {/if}
    </div>
  </BaseModal>
{/if}

<style>
  .admin-dashboard { display: flex; flex-direction: column; height: 100vh; background-color: #f8f9fa; }
  .main-content { flex-grow: 1; padding: 1rem 2rem; overflow-y: auto; background-color: #f8f9fa; min-height: calc(100vh - 80px); max-width: 100%; margin: 0 auto; width: 100%; }
  .container { max-width: 100%; margin: 0 auto; padding: 0; width: 100%; }
  .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding: 1.5rem; background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .page-header h1 { font-size: 1.5rem; font-weight: 700; color: #2c3e50; margin: 0; }
  .header-actions .checkbox-label { display: flex; align-items: center; font-size: 0.9rem; color: #6c757d; }
  .header-actions input[type="checkbox"] { margin-right: 0.5rem; }
  .content-wrapper { display: grid; grid-template-columns: 320px 1fr; gap: 1.5rem; height: calc(100vh - 180px); }
  
  /* 프로젝트 목록 컨테이너 */
  .project-list-container { background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 0; display: flex; flex-direction: column; height: 100%; overflow: hidden; }
  .search-box { padding: 1.5rem 1.5rem 0 1.5rem; margin-bottom: 1rem; }
  .search-input-wrapper { position: relative; }
  .search-box input { width: 100%; padding: 12px 40px 12px 16px; border: 1px solid #e9ecef; border-radius: 8px; font-size: 14px; background-color: #f8f9fa; transition: all 0.2s ease; }
  .search-box input:focus { outline: none; border-color: #4A90E2; background-color: #fff; box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1); }
  .clear-search-btn { 
    position: absolute; 
    right: 12px; 
    top: 50%; 
    transform: translateY(-50%); 
    background: transparent; 
    border: none; 
    font-size: 18px; 
    cursor: pointer; 
    color: #6c757d;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
  }
  .clear-search-btn:hover {
    color: #343a40;
  }
  .project-list-header { padding: 1.5rem 1.5rem 0.5rem 1.5rem; font-size: 16px; font-weight: 700; color: #2c3e50; border-bottom: none; }
  .project-list { flex-grow: 1; overflow-y: auto; padding: 0 1.5rem 1.5rem 1.5rem; }
  .project-list ul { list-style: none; padding: 0; margin: 0; }
  .project-list li { margin-bottom: 8px; border-radius: 8px; transition: all 0.2s ease; border: 1px solid transparent; }
  .project-list button { width: 100%; background: none; border: none; padding: 12px 16px; margin: 0; font: inherit; cursor: pointer; text-align: left; border-radius: 8px; transition: all 0.2s ease; }
  .project-list li:hover { background-color: #f8f9fa; border-color: #e9ecef; }
  .project-list li .selected { background-color: #e3f2fd; border-color: #4A90E2; color: #1976d2; }
  .project-info { display: flex; width: 100%; }
  .project-main-info { flex-grow: 1; display: flex; flex-direction: column; gap: 4px; }
  .project-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 500; font-size: 14px; color: #2c3e50; line-height: 1.2; }
  .company-row { display: flex; justify-content: space-between; align-items: center; }
  .company-name { font-size: 12px; color: #6c757d; font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2; flex-grow: 1; }
  .applicant-count { margin-left: 0.5rem; font-size: 12px; color: #4A90E2; font-weight: 600; background-color: #e3f2fd; padding: 2px 8px; border-radius: 12px; flex-shrink: 0; }
  
  /* 지원자 목록 컨테이너 */
  .applicant-list-container { background-color: #fff; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 1.5rem; display: flex; flex-direction: column; height: 100%; }
  .applicant-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 1rem; border-bottom: 1px solid #e9ecef; }
  .applicant-header h2 { font-size: 1.3rem; font-weight: 700; margin: 0; color: #2c3e50; }
  
  /* 프로젝트 테이블 스타일 */
  .projects-table-container { width: 100%; height: 100%; overflow-y: auto; }
  .projects-table { width: 100%; border-collapse: collapse; background-color: #fff; border-radius: 8px; overflow: hidden; border: 1px solid #e9ecef; }
  .projects-table th { background-color: #f8f9fa; padding: 12px; text-align: left; font-weight: 600; color: #495057; border-bottom: 2px solid #e9ecef; }
  .projects-table td { padding: 12px; border-bottom: 1px solid #f8f9fa; }
  .projects-table tr:hover { background-color: #f8f9fa; }
  .project-title-link { background: none; border: none; color: #4A90E2; cursor: pointer; font-weight: 500; text-decoration: none; }
  .project-title-link:hover { text-decoration: underline; }
  
  /* 모바일 프로젝트 카드 스타일 */
  .mobile-projects-cards { display: flex; flex-direction: column; gap: 1rem; }
  .mobile-project-card { border: 1px solid #e9ecef; border-radius: 12px; padding: 1.5rem; background-color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }

  
  /* 상태 태그 스타일링 */
  :global(.applicant-blacklist-tag), :global(.blacklist-tag) {
    background-color: #dc3545;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
  }
  :global(.blacklist-tag) {
    margin-left: 8px;
  }

  /* 버튼 스타일 */
  :global(.profile-docs-btn), :global(.detail-btn) { 
    padding: 6px 12px; 
    border: none; 
    border-radius: 6px; 
    color: white; 
    cursor: pointer; 
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  :global(.profile-docs-btn.small), :global(.detail-btn.small) {
    padding: 4px 8px;
    font-size: 11px;
  }
  :global(.profile-docs-btn:hover), :global(.detail-btn:hover) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  :global(.detail-btn) { 
    background-color: #28a745; 
  }
  :global(.detail-btn:hover) {
    background-color: #218838;
  }

  /* 상태 선택 드롭다운 스타일링 */
  :global(.status-select), :global(.mobile-status-select) {
    width: 100%; 
    padding: 6px 8px; 
    border-radius: 6px; 
    border: 1px solid #e9ecef; 
    font-size: 13px;
    background-color: #fff;
    transition: all 0.2s ease;
    min-width: 100px;
  }
  :global(.status-select:focus), :global(.mobile-status-select:focus) {
    outline: none;
    border-color: #4A90E2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }

  /* 이름 셀 스타일링 */
  :global(.applicant-name) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  :global(.applicant-name-text) {
    font-weight: 500;
    color: #2c3e50;
  }
  
  /* 모달 스타일 */
  
  
  
  @media (max-width: 768px) {
    .main-content { padding: 1rem; }
    .content-wrapper { 
      grid-template-columns: 1fr; 
      height: auto; 
      gap: 1rem;
    }
    .project-list-container { 
      height: 300px; 
      margin-bottom: 1rem; 
    }
    .applicant-list-container { height: auto; }
    .page-header { 
      padding: 1rem; 
      margin-bottom: 1rem;
    }
    .page-header h1 { font-size: 1.3rem; }
  }
</style>
